import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

export const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

// "Juin 2027" from date_iso, else date_label, else year.
export const editionMonthYear = (ed) => {
  if (!ed) return "";
  if (ed.date_iso) {
    const d = new Date(ed.date_iso);
    if (!isNaN(d.getTime())) {
      const m = MONTHS_FR[d.getMonth()];
      return `${m.charAt(0).toUpperCase()}${m.slice(1)} ${d.getFullYear()}`;
    }
  }
  return ed.date_label || (ed.year ? String(ed.year) : "");
};

// Sortable timestamp for an edition (date_iso first, then year).
export const edSortKey = (ed) => {
  if (ed?.date_iso) {
    const d = new Date(ed.date_iso);
    if (!isNaN(d.getTime())) return d.getTime();
  }
  if (ed?.year) return new Date(ed.year, 0, 1).getTime();
  return -Infinity;
};

// Last real edition strictly before the active one (excludes demo/test/placeholder).
export const previousRealEdition = (editions = [], active) => {
  if (!active) return null;
  const key = edSortKey(active);
  return (
    editions
      .filter((e) => e.id !== active.id && !e.is_demo && edSortKey(e) < key)
      .sort((a, b) => edSortKey(b) - edSortKey(a))[0] || null
  );
};

// 3-state registration status driven by the back-office.
export const regStatus = (ed) =>
  ed?.registration_status_effective || (ed?.registration_open_effective ? "ouvert" : "preinscription");

// Single source of truth: the featured edition (BO checkbox « Édition active du site »).
export const featuredEdition = (editions = []) =>
  editions.find((e) => e.is_active) ||
  editions.find((e) => e.status === "active") ||
  editions.find((e) => e.status === "upcoming") ||
  null;

// Home mode is DERIVED from the featured edition status — no manual toggle.
// past -> retrospective ("after"), everything else -> upcoming layout ("before").
export const homeMode = (featured) => (featured?.status === "past" ? "after" : "before");

// Registration CTA: always points to the right edition.
// - featured upcoming/active -> the featured edition itself.
// - featured past -> the next upcoming edition (so we never invite to a past edition).
export const registrationCta = (featured, next) => {
  const isPast = featured?.status === "past";
  const target = isPast ? next : featured;
  if (!target) return { show: false, status: "none" };
  const status = regStatus(target);
  const year = target.year;
  if (status === "ferme") return { show: false, status, target };
  if (status === "ouvert") {
    const to = target.registration_url || "/inscription";
    return {
      show: true, status, target, to, external: /^https?:/i.test(to),
      label: isPast ? `S'inscrire à l'édition ${year}` : "S'inscrire",
      labelShort: isPast ? `S'inscrire ${year}` : "S'inscrire",
    };
  }
  // preinscription -> interest form of the next upcoming edition
  return {
    show: true, status, target, to: "/edition-2028", external: false,
    label: isPast ? `Être informé pour ${year}` : "Être informé",
    labelShort: isPast ? `Être informé ${year}` : "Être informé",
  };
};

// Single source of truth for editions on the front — no hardcoded years.
export const useEditions = () => {
  const { data: editions = [] } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const active = featuredEdition(editions);
  const next = editions.find((e) => e.status === "upcoming");
  return { editions, active, next };
};
